import { useCallback, useMemo } from 'react';
import { paths } from 'ramda';

import { usePortletConfig } from '@core/js/components/system/Portlet';

import { storageService } from '@core/js/services/storage.service';

import { useOnScreen } from '@interlayer/js/app/hooks/device/hooks/useOnScreen';
import { useGetDataByDeviceAndAuth } from '@interlayer/js/app/hooks/useDataByDeviceAndAuth';

import {
    IMAGE_SELECTOR,
    IMAGE_SELECTOR_GUEST,
    IMAGE_SELECTOR_GUEST_MOBILE,
    IMAGE_SELECTOR_MOBILE,
    IMAGE_SIZE_1X1,
    IMAGE_SIZE_2X2 } from '../constants';
import { getRandomColor } from '../helpers';

export const useBackgroundTile = ({ gameTileRef, windowRef, isForcedShown, inlineGrid, gameData }) => {
    const getDataByDeviceAndAuth = useGetDataByDeviceAndAuth();
    const [gameTileSizes] = paths([['gamesConfiguration', 'gameTileSizes']], usePortletConfig());

    const checkedSize = useMemo(() => {
        const currentTileSize = gameTileSizes?.find(item => item.gameCode === gameData.gameCode);

        if (inlineGrid === 1) {
            return IMAGE_SIZE_1X1;
        }

        return gameData.tileSize || currentTileSize?.tileSize || IMAGE_SIZE_1X1;
    }, [gameData.gameCode, gameData.tileSize, gameTileSizes, inlineGrid]);

    const checkImageUrl = useCallback((size) => {
        const urlForGuest = gameData[IMAGE_SELECTOR_GUEST]?.[`c${size}`];
        const urlForGuestMobile = gameData[IMAGE_SELECTOR_GUEST_MOBILE]?.[`c${size}`];
        const urlForUser = gameData[IMAGE_SELECTOR]?.[`c${size}`];
        const urlForUserMobile = gameData[IMAGE_SELECTOR_MOBILE]?.[`c${size}`];

        const mobileUrlForGuest = urlForGuestMobile || urlForGuest;

        return getDataByDeviceAndAuth({
            logInMobile: urlForUserMobile || urlForUser || mobileUrlForGuest,
            logInFallback: urlForUser || urlForGuest || urlForUserMobile || urlForGuestMobile,
            logOutMobile: mobileUrlForGuest,
            logOutFallback: urlForGuest || urlForGuestMobile
        });
    }, [gameData, getDataByDeviceAndAuth]);

    const { tileSize, imageUrl } = useMemo(() => {
        const checkedImage = checkImageUrl(checkedSize);

        return {
            imageUrl: checkedImage || checkImageUrl(IMAGE_SIZE_1X1),
            tileSize: checkedImage || checkedSize === IMAGE_SIZE_2X2 ? checkedSize : IMAGE_SIZE_1X1
        };
    }, [checkImageUrl, checkedSize]);

    const isOnScreen = useOnScreen({ ref: gameTileRef, root: windowRef?.current, isForced: isForcedShown });

    const backgroundImage = useMemo(() => isOnScreen && imageUrl ? `url('${imageUrl}')` : '', [imageUrl, isOnScreen]);

    return useMemo(() => ({
        tileSize,
        backgroundData: {
            backgroundImage,
            backgroundImageColor: storageService.local.getItem('colourGameItems')
                ? getRandomColor()
                : 'none'
        }
    }), [backgroundImage, tileSize]);
};
